import { FunctionComponent, useMemo, useState } from "react";

import { IconHomeSearch, IconSearch } from "@tabler/icons-react";
import { getLeftNavExpandedPref, LeftNav as LeftNavCore, NavGroup, NavItemProps } from "am-tax-fe-core";
import { useTranslation } from "react-i18next";
import { useMatch } from "react-router-dom";

import { useCurrentUser, useNavItems } from "../../api/index.ts";
import EngageLogoCollapsed from "../../assets/images/engage-logo-white-collapsed.svg?react";
import EngageLogoExpanded from "../../assets/images/engage-logo-white-expanded.svg?react";
import { NavArea } from "../../types/enums";

import { getDynamicNavItem } from "./hooks/app-utils.tsx";
import { useEngagementAppItems } from "./hooks/useEngagementAppItems.tsx";
import { useEngagementOrProjectGroup } from "./hooks/useEngagementOrProjectGroup.tsx";
import { useProjectAppItems } from "./hooks/useProjectAppItems.tsx";
import NavFooter from "./NavFooter.tsx";

export const LeftNav: FunctionComponent = () => {
    const { t } = useTranslation("LeftNav");

    const currentUserQuery = useCurrentUser();
    const currentUser = currentUserQuery.data;

    const matchesEngagement = useMatch("/engagement/:engagementId/*");
    const engagementIdParam = matchesEngagement?.params?.engagementId ?? "";

    const matchesProject = useMatch("/engagement/:engagementId/project/:projectId/*");
    const projectIdParam = matchesProject?.params?.projectId ?? "";

    const [expanded, setExpandedState] = useState(getLeftNavExpandedPref());

    const logo = useMemo(() => {
        return expanded ? <EngageLogoExpanded width="200px" height="60px" /> : <EngageLogoCollapsed width="2rem" height="2rem" />;
    }, [expanded]);

    const { data } = useNavItems();
    const topItems = useMemo(() => (data ?? []).filter(item => item.area === NavArea.Top), [data]);

    // this is costs 2MB in page weight and no one is using it, so I'm disabling for now
    /*
        const icons = useMemo(() => ({}), []);

        const hasDynmaicNavItems = useMemo(() => {
            return !!navItemsQuery.data?.length || !!engagementNavItemsQuery.data?.length;
        }, [navItemsQuery.data, engagementNavItemsQuery.data]);

        const [icons, setIcons] = useState<Record<string, FC<{ size: string }>>>({});
        useEffect(() => {
            if (hasDynmaicNavItems) {
                import("@tabler/icons-react").then(module => {
                    // @ts-expect-error - not worth the effort to get the types to work here
                    setIcons(module);
                });
            }
        }, [hasDynmaicNavItems]);
    */

    const { group: engagementOrProjectNavGroup } = useEngagementOrProjectGroup(engagementIdParam, projectIdParam);

    const { items: engagementItems } = useEngagementAppItems(engagementIdParam, !projectIdParam);
    const { items: projectItems } = useProjectAppItems(engagementIdParam, projectIdParam);
    let appItems: Array<NavGroup | NavItemProps> = [];
    if (projectIdParam) {
        appItems = projectItems;
    } else if (engagementIdParam) {
        appItems = engagementItems;
    }

    const items = useMemo(() => {
        const items: Array<NavGroup | NavItemProps> = [
            { to: "/myEngagements", leftIcon: <IconHomeSearch size="1rem" />, text: t("myEngagements") },
            // { to: "/engagementRollup", leftIcon: <IconAssembly size="1rem" />, text: "Engagement Rollup" },
        ];

        if (currentUser?.isInternal) {
            items.push({ to: "/search", leftIcon: <IconSearch size="1rem" />, text: t("search") });
        }

        if (topItems.length) {
            for (const navItem of topItems) {
                items.push(getDynamicNavItem({ navItem }));
            }
        }

        if (engagementIdParam || projectIdParam) {
            items.push(engagementOrProjectNavGroup);
        }

        return items;
    }, [currentUser?.isInternal, topItems, engagementIdParam, projectIdParam, engagementOrProjectNavGroup, t]);

    const itemsFooter = <NavFooter showAdmin={currentUser?.isInternal ?? false} showInternalDocs={currentUser?.isInternal ?? false} expanded={expanded} />;

    return <LeftNavCore logo={logo} items={items.concat(appItems)} itemsFooter={itemsFooter} onSizeChange={setExpandedState} />;
};
